import { CustomCard } from '@/components/custom-card';
import {
  InfoField,
  PlaceMap,
  PlaceMapPoint,
} from '@/features/harvest/components';
import { VALUE_COLORS } from '@/features/harvest/constants';
import { useHarvestPlacesRegionStats } from '@/features/harvest/hooks';
import { useYearParam } from '@/hooks/useYearParam';
import { cn } from '@/lib/utils';
import { formatNumberOrDash } from '@/utils/numberFormat';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

/** Tashkilot (klaster) ma'lumotlari — oqimning oxirgi bosqichi.
 *  Alohida endpoint yo'q: `type=organization&region_code=…&tax_number=…` javobi,
 *  unda tashkilotning bir nechta maskani kelishi mumkin. */
export const HarvestClusterDetails = () => {
  const { t } = useTranslation();
  const { taxNumber } = useParams();
  const [searchParams] = useSearchParams();
  const year = useYearParam();
  const regionCode = searchParams.get('region') || undefined;

  const { data, isLoading } = useHarvestPlacesRegionStats({
    year,
    regionCode,
    type: 'organization',
    taxNumber,
  });

  /** Tashkilot nomi ro'yxat so'rovidan olinadi (keshdan keladi). */
  const { data: clustersData } = useHarvestPlacesRegionStats({
    year,
    regionCode,
    type: 'organization',
  });
  const cluster = clustersData?.regions?.find(
    (row) => String(row.id) === taxNumber,
  );

  const { data: regionsData } = useHarvestPlacesRegionStats({ year });
  const regionName = regionsData?.regions?.find(
    (row) => String(row.id) === regionCode,
  )?.name_uz;

  const places = data?.regions || [];
  /** Birinchi maskan ochiq turadi, sarlavhaga bosib ochib-yopiladi. */
  const [openPlace, setOpenPlace] = useState<string | undefined>();
  const activePlace = openPlace ?? String(places[0]?.id ?? '');

  /** Koordinatalar matn ko'rinishida keladi va `null` bo'lishi mumkin.
   *  `Number(null)` = 0 bo'lgani uchun bo'sh qiymatni oldin ajratib olamiz,
   *  aks holda marker (0, 0) — okean o'rtasiga tushadi. */
  const points: PlaceMapPoint[] = places.reduce<PlaceMapPoint[]>((acc, row) => {
    const rawLatitude = row.address_details?.latitude;
    const rawLongitude = row.address_details?.longitude;
    if (!rawLatitude || !rawLongitude) return acc;

    const latitude = Number(rawLatitude);
    const longitude = Number(rawLongitude);
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      acc.push({
        id: row.id,
        latitude,
        longitude,
        name: row.name_uz,
        active: String(row.id) === activePlace,
      });
    }
    return acc;
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-[240px] w-full items-center justify-center">
        <div className="progress" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 items-start gap-6">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <InfoField label={`${t('cluster_name')}:`}>
            <span className="text-sm font-medium text-foreground">
              {cluster?.name_uz || '-'}
            </span>
          </InfoField>
          <InfoField label={`${t('legal_tax_number')}:`}>
            <span className="text-sm font-medium text-foreground">
              {taxNumber || '-'}
            </span>
          </InfoField>

          <InfoField label={`${t('harvest_region_name')}:`}>
            <span className="text-sm font-medium text-foreground">
              {regionName || '-'}
            </span>
          </InfoField>
          <InfoField label={`${t('places_count')}:`}>
            <span className={cn('text-sm font-medium', VALUE_COLORS.green)}>
              {places.length} {t('ta')}
            </span>
          </InfoField>
        </div>

        <span className="text-xs font-normal text-foreground opacity-50">
          {t('harvest_locations')}:
        </span>

        {/* Maskan soni ko'p bo'lsa ro'yxat scroll bo'ladi — sahifa cho'zilib ketmaydi. */}
        <div className="sidebar-scrollbar flex max-h-[496px] flex-col gap-2 overflow-y-auto pr-1">
          {places.length ? (
            places.map((place) => {
              const isOpen = activePlace === String(place.id);

              return (
                <CustomCard
                  key={place.id}
                  /* shrink-0 — scrollli flex konteynerda kartalar siqilib qolmasin. */
                  className="flex shrink-0 flex-col overflow-hidden !px-0 !py-0"
                >
                  <button
                    type="button"
                    onClick={() => setOpenPlace(isOpen ? '' : String(place.id))}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                  >
                    <span className="text-sm font-medium text-foreground">
                      {place.name_uz || '-'}
                    </span>
                    <ChevronDown
                      className={cn(
                        'size-4 shrink-0 text-foreground opacity-50 transition-transform duration-200',
                        isOpen && 'rotate-180',
                      )}
                    />
                  </button>

                  {/* grid-rows 0fr -> 1fr: balandlikni bilmasdan silliq ochiladi. */}
                  <div
                    className={cn(
                      'grid transition-[grid-template-rows] duration-300 ease-in-out',
                      isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                    )}
                  >
                    <div className="overflow-hidden">
                      <div className="flex flex-col gap-3 px-4 pb-4">
                        <span className="text-xs font-normal text-foreground opacity-50">
                          {place.address_details?.address || '-'}
                        </span>

                        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                          <InfoField label={`${t('cotton_received')}:`}>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="flex items-center gap-1">
                                <span className="font-normal text-muted-foreground">
                                  {t('physical')}:
                                </span>
                                <span
                                  className={cn(
                                    'font-medium',
                                    VALUE_COLORS.green,
                                  )}
                                >
                                  {formatNumberOrDash(
                                    place.physical_total_net,
                                    ' t',
                                  )}
                                </span>
                              </span>
                              <span className="flex items-center gap-1">
                                <span className="font-normal text-muted-foreground">
                                  {t('conditioned')}:
                                </span>
                                <span
                                  className={cn(
                                    'font-medium',
                                    VALUE_COLORS.green,
                                  )}
                                >
                                  {formatNumberOrDash(
                                    place.conditioned_total_net,
                                    ' t',
                                  )}
                                </span>
                              </span>
                            </div>
                          </InfoField>

                          <InfoField label={`${t('contracts')}:`}>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="flex items-center gap-1">
                                <span className="font-normal text-muted-foreground">
                                  {t('futures')}:
                                </span>
                                <span
                                  className={cn(
                                    'font-medium',
                                    VALUE_COLORS.amber,
                                  )}
                                >
                                  {formatNumberOrDash(place.futures_count)}
                                </span>
                              </span>
                              <span className="flex items-center gap-1">
                                <span className="font-normal text-muted-foreground">
                                  {t('forward')}:
                                </span>
                                <span
                                  className={cn(
                                    'font-medium',
                                    VALUE_COLORS.amber,
                                  )}
                                >
                                  {formatNumberOrDash(place.forward_count)}
                                </span>
                              </span>
                              <span className="flex items-center gap-1">
                                <span className="font-normal text-muted-foreground">
                                  {t('storage')}:
                                </span>
                                <span
                                  className={cn(
                                    'font-medium',
                                    VALUE_COLORS.amber,
                                  )}
                                >
                                  {formatNumberOrDash(place.storage_count)}
                                </span>
                              </span>
                            </div>
                          </InfoField>
                        </div>
                      </div>
                    </div>
                  </div>
                </CustomCard>
              );
            })
          ) : (
            <CustomCard className="flex h-24 items-center justify-center text-sm text-muted-foreground">
              {t('no_data')}
            </CustomCard>
          )}
        </div>
      </div>

      {points.length ? (
        /* Aniq balandlik: `h-full` ota elementda faqat `min-h` bo'lsa nolga tushadi. */
        <PlaceMap
          points={points}
          className="h-[560px] overflow-hidden rounded-[8px]"
        />
      ) : (
        <div className="flex h-[560px] w-full items-center justify-center rounded-[8px] border border-border text-sm text-muted-foreground">
          {t('no_coordinates')}
        </div>
      )}
    </div>
  );
};
